import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, message, Select, Radio, Switch, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import CategoryServ from "../service/CategoryServ";
import SubCategoryServ from "../service/SubCategoryServ";
import GlobalButton from "../util/GlobalButton";
import SafeImage, { getImageUrl } from "./SafeImage";

/* ── Premium Status Toggle ─────────────────────────────────── */
const StatusToggle = ({ checked, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 14px 6px 10px', borderRadius: 20,
                background: checked ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.08)',
                border: `1.5px solid ${checked ? '#86efac' : '#fca5a5'}`,
                transition: 'all 0.25s ease', cursor: 'pointer',
            }}
            onClick={() => onChange && onChange(!checked)}
        >
            <Switch
                checked={checked}
                onChange={onChange}
                size="default"
                style={{ backgroundColor: checked ? '#22c55e' : '#ef4444', minWidth: 40 }}
            />
            <span style={{
                fontSize: 12, fontWeight: 800, letterSpacing: '0.5px',
                color: checked ? '#16a34a' : '#dc2626', textTransform: 'uppercase',
            }}>
                {checked ? 'Active' : 'Inactive'}
            </span>
        </div>
    </div>
);

const AddSubCategory = ({ open, refreshCategory, refreshSubCategory, categoryDetails, isEditing }) => {
    const categoryId = categoryDetails?._id || categoryDetails?.id;
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const [imageError, setImageError] = useState("");
    const [categories, setCategories] = useState([]);
    const [inputType, setInputType] = useState("upload");
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    useEffect(() => { fetchCategories(); }, []);

    const fetchCategories = async () => {
        try {
            const res = await CategoryServ.index({ limit: "all", filter: JSON.stringify({ parentId: null }), select: "-imageUrl" });
            setCategories(res.data || []);
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    useEffect(() => {
        if (isEditing && categoryDetails) {
            form.setFieldsValue({
                name: categoryDetails.name,
                imageUrl: categoryDetails.imageUrl,
                description: categoryDetails.description,
                parentId: categoryDetails.parentId?._id || categoryDetails.parentId,
                isLive: categoryDetails.isLive !== undefined ? categoryDetails.isLive : true
            });
            setImagePreview(categoryDetails.imageUrl || "");
            if (categoryDetails.imageUrl && (categoryDetails.imageUrl.startsWith('http') || categoryDetails.imageUrl.startsWith('https'))) {
                setInputType("url");
            } else {
                setInputType("upload");
            }
        } else {
            form.resetFields();
            setImagePreview("");
            setInputType("upload");
        }
    }, [isEditing, categoryDetails, form]);

    const handleImageUpload = async (file) => {
        try {
            setIsUploadingImage(true);
            setImageError("");
            const formData = new FormData();
            formData.append("image", file);
            const res = await SubCategoryServ.uploadImage(formData);
            if (res && res.imageUrl) {
                const fullUrl = getImageUrl(res.imageUrl);
                setImagePreview(fullUrl);
                form.setFieldsValue({ imageUrl: res.imageUrl });
                message.success("Image uploaded successfully");
            }
        } catch (error) {
            const errorMsg = error.message || error.response?.data?.message || "Failed to upload image";
            setImageError(errorMsg);
            message.error(errorMsg);
        } finally {
            setIsUploadingImage(false);
        }
        return false;
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);
            const payload = {
                name: values.name,
                imageUrl: values.imageUrl,
                description: values.description,
                parentId: values.parentId || null,
                isLive: values.isLive
            };
            if (isEditing && categoryId) {
                await SubCategoryServ.update(categoryId, payload);
            } else {
                await SubCategoryServ.create(payload);
            }
            handleClose(true);
        } catch (error) {
            message.error(error.message || "Failed to save sub category");
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = (shouldRefresh) => {
        if (refreshSubCategory) {
            refreshSubCategory(shouldRefresh);
        } else if (refreshCategory) {
            refreshCategory(shouldRefresh);
        }
        form.resetFields();
        setImagePreview("");
    };

    /* ── Shared label style matching the AddCategory/Shop modal ─ */
    const labelStyle = {
        fontSize: 11, fontWeight: 800, color: '#0B1F4D',
        letterSpacing: '0.4px', textTransform: 'uppercase',
        display: 'block', marginBottom: 6,
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 12,
                        background: 'linear-gradient(135deg, #4776E6 0%, #0B1F4D 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                        </svg>
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1d2e' }}>
                            {isEditing ? "Edit Sub Category" : "Create Sub Category"}
                        </h2>
                        <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', fontWeight: 500, marginTop: 1 }}>
                            {isEditing ? "Update sub category details" : "Standardized Category Management"}
                        </p>
                    </div>
                </div>
            }
            open={open}
            onCancel={() => handleClose(false)}
            width={720}
            className="custom-modal"
            centered
            styles={{
                header: { borderBottom: '3px solid transparent', borderImage: 'linear-gradient(90deg, #FF8800, #FF5E00, #4776E6, #0B1F4D) 1', paddingBottom: 14 },
            }}
            footer={
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0 4px 0', borderTop: '1.5px solid #f1f5f9',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Category Management</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            onClick={() => handleClose(false)}
                            disabled={submitting}
                            style={{
                                height: 38, padding: '0 20px', borderRadius: 8,
                                border: '1.5px solid #e2e8f0', background: '#fff',
                                fontSize: 13, fontWeight: 700, color: '#64748b',
                                cursor: 'pointer',
                            }}
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={() => form.submit()}
                            disabled={submitting}
                            style={{
                                height: 38, padding: '0 28px', borderRadius: 8,
                                border: 'none',
                                background: 'linear-gradient(135deg, #4776E6 0%, #0B1F4D 100%)',
                                fontSize: 13, fontWeight: 800, color: '#fff',
                                cursor: 'pointer', letterSpacing: '0.3px',
                                display: 'flex', alignItems: 'center', gap: 6,
                                boxShadow: '0 4px 14px rgba(71,118,230,0.3)',
                            }}
                        >
                            {isEditing ? "UPDATE" : "CREATE"}
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                            </svg>
                        </button>
                    </div>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                size="middle"
                requiredMark={false}
                onFinish={handleSubmit}
                onFinishFailed={() => message.error("Please fill required fields")}
                onValuesChange={(changedValues) => {
                    if (changedValues.imageUrl !== undefined) {
                        setImagePreview(changedValues.imageUrl);
                    }
                }}
            >
                {/* ── Top: 2-Column Grid ──────────────────────────────── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>

                    {/* ── LEFT COLUMN: Name + Parent Category + Status ── */}
                    <div>
                        {/* Sub Category Name */}
                        <div style={{ marginBottom: 16 }}>
                            <span style={labelStyle}>Sub Category Name <span style={{ color: '#ef4444' }}>*</span></span>
                            <Form.Item name="name" noStyle rules={[{ required: true, message: "Sub category name is required" }]}>
                                <Input
                                    placeholder="Enter sub category name"
                                    style={{ height: 40, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                                />
                            </Form.Item>
                        </div>

                        {/* Parent Category */}
                        <div style={{ marginBottom: 16 }}>
                            <span style={labelStyle}>Parent Category <span style={{ color: '#ef4444' }}>*</span></span>
                            <Form.Item name="parentId" noStyle rules={[{ required: true, message: "Please select a parent category" }]}>
                                <Select
                                    placeholder="Select parent category"
                                    allowClear
                                    showSearch
                                    style={{ width: '100%', height: 40 }}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={categories
                                        .filter(c => c._id !== categoryId)
                                        .map(c => ({ value: c._id, label: c.name }))
                                    }
                                />
                            </Form.Item>
                        </div>

                        {/* Status */}
                        <div style={{ marginTop: 4 }}>
                            <span style={{ ...labelStyle, marginBottom: 8 }}>STATUS</span>
                            <Form.Item name="isLive" valuePropName="checked" initialValue={true} noStyle>
                                <StatusToggle />
                            </Form.Item>
                        </div>
                    </div>

                    {/* ── RIGHT COLUMN: Image Source + Upload ── */}
                    <div>
                        {/* Image Source Toggle */}
                        <div style={{ marginBottom: 10 }}>
                            <span style={labelStyle}>Sub Category Image Source</span>
                            <Radio.Group
                                value={inputType}
                                onChange={(e) => {
                                    setInputType(e.target.value);
                                    const currentVal = form.getFieldValue("imageUrl");
                                    if (e.target.value === 'url' && currentVal && String(currentVal).startsWith('data:')) {
                                        form.setFieldsValue({ imageUrl: '' });
                                        setImagePreview('');
                                    }
                                }}
                            >
                                <Radio value="upload">Upload Image</Radio>
                                <Radio value="url">Use Image URL</Radio>
                            </Radio.Group>
                        </div>

                        {/* Upload / URL input */}
                        {inputType === 'upload' ? (
                            <div style={{ marginBottom: 24 }}>
                                <Form.Item name="imageUrl" rules={[{ required: !isEditing, message: "Sub category image is required" }]} noStyle>
                                    <Input type="hidden" />
                                </Form.Item>
                                <Upload
                                    className="upload-full-width"
                                    beforeUpload={handleImageUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <div style={{
                                        width: '100%', height: 160, borderRadius: 12, overflow: 'hidden',
                                        border: '2px dashed #c7d2fe', cursor: 'pointer', position: 'relative'
                                    }}>
                                        {isUploadingImage ? (
                                            <div style={{ padding: '40px 0', textAlign: 'center', width: '100%', height: '100%', background: '#f8fafc' }}>
                                                <Spin size="large" />
                                                <div style={{ marginTop: 10, color: '#64748b', fontSize: 13, fontWeight: 500 }}>Uploading...</div>
                                            </div>
                                        ) : imagePreview ? (
                                            <div className="banner-image-preview" style={{
                                                width: '100%', height: '100%', position: 'relative'
                                            }}>
                                                <SafeImage src={imagePreview} alt="SubCategory Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                <div className="banner-overlay">
                                                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>Click to Change</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="banner-upload-placeholder" style={{
                                                width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center', background: '#f8fafc',
                                                transition: 'all 0.2s'
                                            }}>
                                                <div style={{
                                                    width: 48, height: 48, borderRadius: '50%', background: '#e0e7ff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
                                                }}>
                                                    <UploadOutlined style={{ fontSize: 24, color: '#4f46e5' }} />
                                                </div>
                                                <div style={{ color: '#334155', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                                                    Upload SubCategory Image
                                                </div>
                                                <div style={{ color: '#94a3b8', fontSize: 12 }}>
                                                    PNG, JPG, WebP up to 3MB
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Upload>
                                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                    Maximum upload size: <strong>3MB</strong> (JPG, PNG, WEBP)
                                </div>
                                {imageError && (
                                    <div style={{ marginTop: 8, fontSize: 13, color: '#ef4444', textAlign: 'center', background: '#fef2f2', padding: '8px', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                                        {imageError}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Form.Item
                                    name="imageUrl"
                                    rules={[{ required: !isEditing, message: "Image URL is required" }]}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Input
                                        placeholder="https://example.com/image.jpg"
                                        style={{ height: 40, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13 }}
                                    />
                                </Form.Item>
                                {imagePreview && (
                                    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0', height: 160 }}>
                                        <SafeImage
                                            src={imagePreview}
                                            alt="Sub Category Preview"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Bottom: Description (full width) ────────────────── */}
                <div style={{ marginTop: 20 }}>
                    <span style={labelStyle}>Sub Category Description</span>
                    <Form.Item name="description" noStyle>
                        <Input.TextArea
                            rows={4}
                            placeholder="Enter sub category description"
                            style={{ borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, resize: 'none' }}
                        />
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default AddSubCategory;
